/**
 * WebMCP runtime for loreto.io.
 *
 * WebMCP (W3C Web Machine Learning CG, built by Google and Microsoft) lets a
 * page hand an AI agent real tools instead of making it scrape the DOM and
 * simulate clicks. The page keeps ownership of the action: the agent asks, our
 * own client code runs, our UI stays in sync.
 *
 * Two things this module exists to absorb:
 *
 * 1. **Surface drift.** The explainer and the shipped Chrome implementation
 *    both put the API on `document.modelContext`. A lot of secondary writing
 *    says `navigator.modelContext`, and some early builds exposed it there. We
 *    look for both rather than betting on one.
 *
 * 2. **The long tail of agents that do not speak WebMCP yet.** Chrome is in
 *    origin trial, Edge is behind a trial, Firefox and Safari have not shipped.
 *    So every tool is also mirrored on `window.__loretoWebMCP`, which any
 *    driver (Claude in Chrome, Playwright, a devtools console) can call today.
 *    That mirror is what the tests drive, and it is the reason this works
 *    outside a WebMCP browser at all.
 */

const MIRROR = '__loretoWebMCP';

/** The native container, wherever this browser decided to put it. */
export function nativeContext() {
  if (typeof document !== 'undefined' && document.modelContext) return document.modelContext;
  // Some pre-trial builds and much of the secondary documentation use this.
  if (typeof navigator !== 'undefined' && navigator.modelContext) return navigator.modelContext;
  return null;
}

export function isSupported() {
  return nativeContext() !== null;
}

/** The in-page mirror, created on demand. Always present, native or not. */
export function mirror() {
  if (typeof window === 'undefined') return null;
  if (!window[MIRROR]) {
    const tools = new Map();
    window[MIRROR] = {
      version: 1,
      get native() {
        return isSupported();
      },
      /** Tool descriptors, without the callbacks — safe to log or serialise. */
      list() {
        return [...tools.values()].map(({ name, description, inputSchema, annotations, group }) => ({
          name,
          description,
          inputSchema,
          annotations,
          group: group || 'public',
        }));
      },
      /** Invoke a tool the way an agent would. Returns the WebMCP result shape. */
      async call(name, args = {}) {
        const tool = tools.get(name);
        if (!tool) throw new Error(`No such tool: ${name}`);
        return tool.execute(args, { signal: undefined });
      },
      _tools: tools,
      _subs: new Set(),
      /** Subscribe to tool set changes. Returns an unsubscribe function. */
      onChange(fn) {
        this._subs.add(fn);
        return () => this._subs.delete(fn);
      },
      _notify() {
        this._subs.forEach((fn) => {
          try { fn(); } catch (err) { console.warn('[webmcp] subscriber threw', err); }
        });
      },
    };
  }
  return window[MIRROR];
}

/** The result shape the WebMCP explainer documents. */
export function toolResult(text, data) {
  const payload = data === undefined ? text : `${text}\n\n${JSON.stringify(data, null, 2)}`;
  return { content: [{ type: 'text', text: payload }] };
}

/**
 * An error the agent can act on.
 *
 * Best practice from the Chrome guide: validate loosely in the schema and
 * strictly in the handler, then say what went wrong in words the model can use
 * to retry. Throwing a raw exception just stalls the agent.
 */
export function toolError(message) {
  return { content: [{ type: 'text', text: `Error: ${message}` }], isError: true };
}

/**
 * Register a set of tools. Returns a disposer.
 *
 * Registration is per page state on purpose — every registered tool spends
 * tokens in the agent's context window, so a listing page registers "buy this
 * skill" and drops it again on the way out.
 */
export function registerTools(tools) {
  const m = mirror();
  const ctx = nativeContext();
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const names = [];

  tools.forEach((tool) => {
    if (!tool || !tool.name || !tool.description || !tool.inputSchema) {
      // registerTool throws InvalidStateError on an empty name or description,
      // and one bad tool would take the whole set down with it.
      console.warn('[webmcp] skipping malformed tool', tool && tool.name);
      return;
    }
    if (m) {
      if (m._tools.has(tool.name)) {
        console.warn('[webmcp] duplicate tool name, skipping:', tool.name);
        return;
      }
      m._tools.set(tool.name, tool);
    }
    names.push(tool.name);

    if (ctx && typeof ctx.registerTool === 'function') {
      try {
        const promise = ctx.registerTool(
          {
            name: tool.name,
            description: tool.description,
            inputSchema: tool.inputSchema,
            annotations: tool.annotations,
            execute: tool.execute,
          },
          controller ? { signal: controller.signal } : undefined
        );
        // A rejected promise here is normal, not exceptional: Permissions-Policy
        // can disable tools, and then registerTool rejects with NotAllowedError.
        if (promise && typeof promise.catch === 'function') {
          promise.catch((err) => console.warn('[webmcp] registerTool refused:', tool.name, err && err.name));
        }
      } catch (err) {
        console.warn('[webmcp] registerTool threw:', tool.name, err && err.message);
      }
    }
  });

  if (m && names.length) m._notify();

  return function dispose() {
    if (controller) controller.abort();
    if (m) {
      names.forEach((n) => m._tools.delete(n));
      m._notify();
    }
  };
}
